import { ProtocolError, validateRunResponse, type RunResponse } from '../sandboxd';
import { stripVizLines } from '../viz';
import { outputsMatch } from './normalize';

/** judge.md §3.1。 */
export type Verdict = 'AC' | 'WA' | 'RE' | 'TLE' | 'CE';
export type VerdictDetail = 'output-limit' | 'start-failed';

export interface CaseResult {
  /** 0 始まり。 */
  index: number;
  verdict: Verdict;
  detail?: VerdictDetail;
  /** 生の stdout。 */
  stdout: string;
  /** @@viz 行を取り除いた stderr（viz-protocol.md §5）。 */
  stderr: string;
  /** CE のときは null。 */
  durationMs: number | null;
}

export interface JudgeResult {
  overall: Verdict;
  cases: CaseResult[];
  acCount: number;
  /** compile.output（警告を含む）。無ければ ""。 */
  compileOutput: string;
}

export interface JudgeCase {
  in: string;
  out: string;
}

export { ProtocolError };

/** sandboxd が出力上限で打ち切ったときに stderr の末尾に付ける文（sandboxd-client.md §2.1）。 */
export const OUTPUT_LIMIT_SUFFIX = '出力が上限を超えたため打ち切りました';
/** プロセス起動失敗の stderr（sandboxd-client.md §2.1）。 */
export const START_FAILED_STDERR = 'failed to start process';

/** overall の重さ（judge.md §3.3）。CE > TLE > RE > WA > AC。 */
const SEVERITY: Record<Verdict, number> = { AC: 0, WA: 1, RE: 2, TLE: 3, CE: 4 };

/** 全体の判定（judge.md §3.3）。空なら AC。 */
export function aggregateVerdicts(verdicts: readonly Verdict[]): Verdict {
  let worst: Verdict = 'AC';
  for (const v of verdicts) if (SEVERITY[v] > SEVERITY[worst]) worst = v;
  return worst;
}

/**
 * sandboxd の応答からケースごとの判定と全体の判定を出す（judge.md §3）。
 * 応答の形が契約に合わないときは {@link ProtocolError} を投げる（§3.4）。
 */
export function judgeCases(cases: readonly JudgeCase[], res: RunResponse): JudgeResult {
  const r = validateRunResponse(res, cases.length);
  const compileOutput = r.compile?.output ?? '';
  if (r.compile?.ok !== true) {
    return {
      overall: 'CE',
      cases: cases.map((_, index) => ({
        index,
        verdict: 'CE',
        stdout: '',
        stderr: '',
        durationMs: null,
      })),
      acCount: 0,
      compileOutput,
    };
  }
  const runs = r.runs ?? [];
  const results: CaseResult[] = cases.map((c, index) => {
    const run = runs[index];
    if (!run) throw new ProtocolError(`runs[${index}] is missing`);
    const base = {
      index,
      stdout: run.stdout,
      stderr: stripVizLines(run.stderr),
      durationMs: run.durationMs,
    };
    if (run.timedOut) return { ...base, verdict: 'TLE' };
    if (run.exitCode !== 0) {
      if (run.stderr.endsWith(OUTPUT_LIMIT_SUFFIX))
        return { ...base, verdict: 'RE', detail: 'output-limit' };
      if (run.stderr === START_FAILED_STDERR)
        return { ...base, verdict: 'RE', detail: 'start-failed' };
      return { ...base, verdict: 'RE' };
    }
    return { ...base, verdict: outputsMatch(run.stdout, c.out) ? 'AC' : 'WA' };
  });
  return {
    overall: aggregateVerdicts(results.map((c) => c.verdict)),
    cases: results,
    acCount: results.filter((c) => c.verdict === 'AC').length,
    compileOutput,
  };
}
